import { useState, useEffect } from "react";
import { Heart, MessageCircle, Share2, Bookmark, Copy, MessageSquare, Link2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface PostActionsProps {
  postId: string;
  initialLikes: number;
  initialComments: number;
  onCommentClick?: () => void;
  compact?: boolean;
  className?: string;
}

export const PostActions = ({
  postId,
  initialLikes,
  initialComments,
  onCommentClick,
  compact = false,
  className,
}: PostActionsProps) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(initialLikes);
  const [isSaved, setIsSaved] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  useEffect(() => {
    if (user) {
      checkUserInteractions();
    }
  }, [user, postId]);

  const checkUserInteractions = async () => {
    if (!user) return;

    // Check if user liked this post
    const { data: likeData } = await supabase
      .from("post_likes")
      .select("id")
      .eq("post_id", postId)
      .eq("user_id", user.id)
      .maybeSingle();

    setIsLiked(!!likeData);

    // Check if user saved this post
    const { data: saveData } = await supabase
      .from("saved_posts")
      .select("id")
      .eq("post_id", postId)
      .eq("user_id", user.id)
      .maybeSingle();

    setIsSaved(!!saveData);
  };

  const handleLike = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      toast.error("Please sign in to like posts");
      return;
    }

    setIsLikeLoading(true);

    if (isLiked) {
      // Unlike
      const { error } = await supabase
        .from("post_likes")
        .delete()
        .eq("post_id", postId)
        .eq("user_id", user.id);

      if (!error) {
        setIsLiked(false);
        setLikesCount((prev) => Math.max(0, prev - 1));
        
        // Update community_posts likes_count
        await supabase
          .from("community_posts")
          .update({ likes_count: Math.max(0, likesCount - 1) })
          .eq("id", postId);
      }
    } else {
      // Like
      const { error } = await supabase
        .from("post_likes")
        .insert({ post_id: postId, user_id: user.id });

      if (!error) {
        setIsLiked(true);
        setLikesCount((prev) => prev + 1);
        
        // Update community_posts likes_count
        await supabase
          .from("community_posts")
          .update({ likes_count: likesCount + 1 })
          .eq("id", postId);
      }
    }

    setIsLikeLoading(false);
  };

  const handleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!user) {
      toast.error("Please sign in to save posts");
      return;
    }

    setIsSaveLoading(true);

    if (isSaved) {
      // Unsave
      const { error } = await supabase
        .from("saved_posts")
        .delete()
        .eq("post_id", postId)
        .eq("user_id", user.id);

      if (!error) {
        setIsSaved(false);
        toast.success("Removed from saved");
      }
    } else {
      // Save
      const { error } = await supabase
        .from("saved_posts")
        .insert({ post_id: postId, user_id: user.id });

      if (!error) {
        setIsSaved(true);
        toast.success("Saved to your collection");
      }
    }

    setIsSaveLoading(false);
  };

  const handleShare = (method: "copy" | "sms" | "email" | "twitter") => {
    const postUrl = `${window.location.origin}/community?post=${postId}`;
    const shareText = "Check out this post on Comp3t3!";

    switch (method) {
      case "copy":
        navigator.clipboard.writeText(postUrl);
        toast.success("Link copied to clipboard");
        break;
      case "sms":
        window.open(`sms:?body=${encodeURIComponent(`${shareText} ${postUrl}`)}`);
        break;
      case "email":
        window.open(`mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(postUrl)}`);
        break;
      case "twitter":
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(postUrl)}`);
        break;
    }
  };

  const handleCommentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onCommentClick?.();
  };

  const iconSize = compact ? "w-4 h-4" : "w-5 h-5";
  const textSize = compact ? "text-xs" : "text-sm";
  const gap = compact ? "gap-1" : "gap-1.5";

  return (
    <div className={cn("flex items-center justify-between", className)}>
      <div className={cn("flex items-center", compact ? "gap-3" : "gap-4")}>
        {/* Like Button */}
        <button
          onClick={handleLike}
          disabled={isLikeLoading}
          aria-label={isLiked ? "Unlike post" : "Like post"}
          className={cn(
            "flex items-center transition-colors min-h-6 min-w-6",
            gap,
            textSize,
            isLiked ? "text-red-500" : "text-muted-foreground hover:text-red-500"
          )}
        >
          <Heart className={cn(iconSize, isLiked && "fill-current")} />
          <span>{likesCount}</span>
        </button>

        {/* Comment Button */}
        <button
          onClick={handleCommentClick}
          aria-label="View comments"
          className={cn(
            "flex items-center text-muted-foreground hover:text-primary transition-colors min-h-6 min-w-6",
            gap,
            textSize
          )}
        >
          <MessageCircle className={iconSize} />
          <span>{initialComments}</span>
        </button>
      </div>

      <div className={cn("flex items-center", compact ? "gap-2" : "gap-3")}>
        {/* Save Button */}
        <button
          onClick={handleSave}
          disabled={isSaveLoading}
          aria-label={isSaved ? "Remove from saved" : "Save post"}
          className={cn(
            "transition-colors min-h-6 min-w-6 flex items-center justify-center",
            isSaved ? "text-primary" : "text-muted-foreground hover:text-primary"
          )}
        >
          <Bookmark className={cn(iconSize, isSaved && "fill-current")} />
        </button>

        {/* Share Button */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              onClick={(e) => e.stopPropagation()}
              aria-label="Share post"
              className="text-muted-foreground hover:text-primary transition-colors min-h-6 min-w-6 flex items-center justify-center"
            >
              <Share2 className={iconSize} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => handleShare("copy")}>
              <Link2 className="w-4 h-4 mr-2" />
              Copy Link
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare("sms")}>
              <MessageSquare className="w-4 h-4 mr-2" />
              Share via SMS
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare("email")}>
              <Copy className="w-4 h-4 mr-2" />
              Share via Email
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleShare("twitter")}>
              <Share2 className="w-4 h-4 mr-2" />
              Share on Twitter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
